<template>
    <div class="tw:px-6 tw:py-6">
        <!-- Division badge -->
        <div class="tw:mb-4">
            <Badge variant="info" :label="$t('back.dashboard.standings.division_badge')" />
        </div>

        <!-- ==================== Standings Section ==================== -->
        <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.standings.section_title') }}</h3>
        <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.standings.section_description') }}</p>

        <!-- Evolución de puntos acumulados -->
        <div v-if="puntosEvolucion.length > 1" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-8">
            <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">Evolución de Puntos</h3>
            <p class="tw:text-sm tw:text-gray-500 tw:mb-4">Puntos acumulados por jornada</p>
            <Chart
                type="LineChart"
                :data="puntosEvolucion"
                :options="evolucionChartOptions"
                :height="300"
            />
        </div>

        <!-- Standing error -->
        <div v-if="standingsError" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ standingsError }}</span>
        </div>

        <!-- Standings loading skeleton -->
        <div v-if="standingsLoading" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-8">
            <div v-for="i in 5" :key="i" class="tw:h-8 tw:bg-gray-100 tw:animate-pulse tw:rounded tw:mb-2"></div>
        </div>

        <!-- Standings table -->
        <div v-else-if="standingsTable.length > 0" class="tw:border tw:border-gray-200 tw:rounded-lg tw:overflow-hidden tw:bg-white tw:mb-8">
            <div class="tw:overflow-x-auto">
                <table class="tw:min-w-full tw:divide-y tw:divide-gray-200">
                    <thead class="tw:bg-gray-50">
                        <tr>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-center">#</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-left">{{ $t('back.dashboard.standings.col_club') }}</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">PJ</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">PG</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">PE</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">PP</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">GF</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">GC</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right">DG</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-right tw:font-bold">Pts</th>
                            <th class="tw:px-3 tw:py-3 tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:text-center">{{ $t('back.dashboard.standings.col_streak') }}</th>
                        </tr>
                    </thead>
                    <tbody class="tw:divide-y tw:divide-gray-200">
                        <tr v-for="(row, idx) in standingsTable" :key="'st-' + idx" class="hover:tw:bg-gray-50">
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-center tw:font-bold" :class="row.posicion <= 3 ? 'tw:text-green-600' : 'tw:text-gray-700'">{{ row.posicion }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-900 tw:flex tw:items-center tw:gap-2">
                                <img v-if="row.shield" :src="row.shield" alt="" class="tw:w-5 tw:h-5 tw:object-contain" />
                                <span>{{ row.clubName }}</span>
                            </td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700 tw:text-right">{{ row.pj }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700 tw:text-right">{{ row.pg }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700 tw:text-right">{{ row.pe }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700 tw:text-right">{{ row.pp }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700 tw:text-right">{{ row.gf }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700 tw:text-right">{{ row.gc }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-right tw:font-medium" :class="row.dg > 0 ? 'tw:text-green-600' : row.dg < 0 ? 'tw:text-red-600' : 'tw:text-gray-700'">{{ row.dg > 0 ? '+' : '' }}{{ row.dg }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-right tw:font-bold tw:text-gray-900">{{ row.puntos }}</td>
                            <td class="tw:px-3 tw:py-2 tw:text-sm tw:text-center">
                                <span v-if="row.racha" class="tw:inline-flex tw:gap-0.5">
                                    <span v-for="(result, ri) in row.racha.split('')" :key="ri"
                                          class="tw:w-5 tw:h-5 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-xs tw:font-bold tw:text-white"
                                          :class="RACHA_MAP[result]?.bgClass || 'tw:bg-red-500'"
                                    >
                                        {{ RACHA_MAP[result]?.label || result.toUpperCase() }}
                                    </span>
                                </span>
                                <span v-else class="tw:text-gray-400">—</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ==================== Results Section ==================== -->
        <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1 tw:mt-8">{{ $t('back.dashboard.standings.results_title') }}</h3>
        <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.standings.results_description') }}</p>

        <!-- Results error -->
        <div v-if="resultsError" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ resultsError }}</span>
        </div>

        <!-- Results loading -->
        <div v-if="resultsLoading" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
            <div v-for="i in 3" :key="i" class="tw:h-16 tw:bg-gray-100 tw:animate-pulse tw:rounded tw:mb-3"></div>
        </div>

        <!-- Results cards -->
        <div v-else-if="groupedResults.length > 0" class="tw:space-y-6">
            <div v-for="group in groupedResults" :key="group.jornada" class="tw:border tw:border-gray-200 tw:rounded-lg tw:bg-white tw:overflow-hidden">
                <div class="tw:bg-gray-50 tw:px-4 tw:py-2 tw:border-b tw:border-gray-200">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700">{{ $t('back.dashboard.standings.matchday') }} {{ group.jornada }}</h4>
                </div>
                <div class="tw:divide-y tw:divide-gray-100">
                    <div v-for="match in group.matches" :key="match.jornada + '-' + match.localName + '-' + match.visitanteName"
                         class="tw:px-4 tw:py-3 tw:flex tw:items-center tw:justify-between tw:gap-4"
                         :class="{
                             'tw:bg-green-50': isWinForClub(match),
                             'tw:bg-red-50': isLossForClub(match),
                             'tw:bg-gray-50': isDraw(match),
                         }"
                    >
                        <!-- Local -->
                        <div class="tw:flex tw:items-center tw:gap-2 tw:w-2/5 tw:justify-end">
                            <span class="tw:text-sm tw:font-medium tw:text-gray-900 tw:text-right">{{ match.localName }}</span>
                            <img v-if="match.localShield" :src="match.localShield" alt="" class="tw:w-6 tw:h-6 tw:object-contain" />
                        </div>

                        <!-- Score -->
                        <div class="tw:flex tw:items-center tw:gap-3 tw:w-1/5 tw:justify-center">
                            <template v-if="match.status === 'completed'">
                                <span class="tw:text-lg tw:font-bold tw:text-gray-900">{{ match.golesLocal ?? '-' }}</span>
                                <span class="tw:text-gray-400">—</span>
                                <span class="tw:text-lg tw:font-bold tw:text-gray-900">{{ match.golesVisitante ?? '-' }}</span>
                            </template>
                            <template v-else>
                                <span class="tw:text-sm tw:text-gray-400 tw:italic">{{ $t('back.dashboard.standings.pending') }}</span>
                            </template>
                        </div>

                        <!-- Visitor -->
                        <div class="tw:flex tw:items-center tw:gap-2 tw:w-2/5">
                            <img v-if="match.visitanteShield" :src="match.visitanteShield" alt="" class="tw:w-6 tw:h-6 tw:object-contain" />
                            <span class="tw:text-sm tw:font-medium tw:text-gray-900">{{ match.visitanteName }}</span>
                        </div>

                        <!-- Date & status -->
                        <div class="tw:flex tw:flex-col tw:items-end tw:w-1/5">
                            <span class="tw:text-xs tw:text-gray-400">{{ formatDate(match.date) }}</span>
                            <Badge
                                v-if="match.status === 'completed'"
                                variant="success"
                                :label="$t('back.dashboard.standings.played')"
                            />
                            <Badge v-else variant="warning" :label="$t('back.dashboard.standings.pending')" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty results -->
        <div v-else class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-8 tw:text-center">
            <p class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.standings.results_empty') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import { useStandings } from '@/composables/admin/dashboard/useStandings';
    import { useResults } from '@/composables/admin/dashboard/useResults';
    import { formatDate } from '@/utils/format';
    import { RACHA_MAP } from '@/composables/common/rachaMap';
    import Badge from '@/modules/admin/ui/Badge.vue';
    import Chart from '@/modules/admin/ui/Chart.vue';



    const {
        puntosChartData,
        standingsTable,
        loading: standingsLoading,
        error: standingsError,
    } = useStandings();

    const {
        groupedResults,
        loading: resultsLoading,
        error: resultsError,
    } = useResults();

    /** ID de nuestro club detectado desde los resultados (el que más partidos juega = aparece en casi todas las jornadas) */
    const ourClubId = computed(() => {
        const freq = {};
        for (const group of groupedResults.value) {
            for (const m of group.matches) {
                if (m.localId && m.localId > 0) freq[m.localId] = (freq[m.localId] || 0) + 1;
                if (m.visitanteId && m.visitanteId > 0) freq[m.visitanteId] = (freq[m.visitanteId] || 0) + 1;
            }
        }
        let maxId = null;
        let maxCount = 0;
        for (const [id, count] of Object.entries(freq)) {
            if (count > maxCount) { maxCount = count; maxId = Number(id); }
        }
        return maxId;
    });

    /** Determina si un partido completado es victoria para nuestro club */
    function isWinForClub(match) {
        if (match.status !== 'completed') return false;
        if (match.localId === ourClubId.value) return match.golesLocal > match.golesVisitante;
        if (match.visitanteId === ourClubId.value) return match.golesVisitante > match.golesLocal;
        return false;
    }

    /** Determina si un partido completado es derrota para nuestro club */
    function isLossForClub(match) {
        if (match.status !== 'completed') return false;
        if (match.localId === ourClubId.value) return match.golesLocal < match.golesVisitante;
        if (match.visitanteId === ourClubId.value) return match.golesVisitante < match.golesLocal;
        return false;
    }

    /** Determina si un partido completado es empate */
    function isDraw(match) {
        if (match.status !== 'completed') return false;
        return match.golesLocal === match.golesVisitante;
    }

    /** Datos del chart de evolución de puntos */
    const puntosEvolucion = computed(() => {
        const results = groupedResults.value;
        const clubId = ourClubId.value;
        if (!results.length || !clubId) return [];

        let accumulated = 0;
        const chartData = [['Jornada', 'Puntos Acumulados']];

        for (const group of results) {
            const ourMatches = group.matches.filter(m =>
                m.status === 'completed' &&
                (m.localId === clubId || m.visitanteId === clubId)
            );

            if (ourMatches.length > 0) {
                for (const match of ourMatches) {
                    let points = 0;
                    if (match.localId === clubId) {
                        if (match.golesLocal > match.golesVisitante) points = 3;
                        else if (match.golesLocal === match.golesVisitante) points = 1;
                    } else {
                        if (match.golesVisitante > match.golesLocal) points = 3;
                        else if (match.golesLocal === match.golesVisitante) points = 1;
                    }
                    accumulated += points;
                }
                chartData.push([`J ${group.jornada}`, accumulated]);
            }
        }

        return chartData;
    });

    const evolucionChartOptions = {
        legend: { position: 'none' },
        colors: ['#2563eb'],
        chartArea: { width: '85%', height: '70%' },
        pointSize: 5,
        pointShape: 'circle',
        lineWidth: 2,
        vAxis: { title: 'Puntos', minValue: 0, viewWindow: { min: 0 } },
        hAxis: { textPosition: 'out', slantedText: true, slantedTextAngle: 45 },
    };
</script>
